import { z } from "zod";
import { signedKvGet, signedKvPut } from "@ease/shared";
import { REENTRY_POLICIES, CONSENT_REQUIREMENTS } from "./types.js";
import type { TriggerIndex, TriggerIndexEntry } from "./trigger-types.js";

// ---------------------------------------------------------------------------
// KV interface — minimal subset to avoid depending on @cloudflare/workers-types
// ---------------------------------------------------------------------------

/**
 * Minimal KVNamespace interface covering only the methods we use.
 * Allows the package to stay free of @cloudflare/workers-types.
 */
export interface KVNamespaceLike {
  get(key: string): Promise<string | null>;
  put(key: string, value: string, opts?: { expirationTtl?: number }): Promise<void>;
  delete(key: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// Trigger source — pluggable data fetcher for rebuild
// ---------------------------------------------------------------------------

/**
 * Async function that fetches all trigger entries for a tenant.
 * Returns a Map of eventType -> TriggerIndexEntry[].
 *
 * In production this queries the database; in tests it returns mock data.
 */
export type TriggerSource = (
  tenantId: string,
) => Promise<Map<string, TriggerIndexEntry[]>>;

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface KVTriggerIndexConfig {
  /**
   * Data source used by rebuild() to fetch current trigger entries.
   * When the DB layer is wired up, this queries active workflows.
   */
  triggerSource: TriggerSource;
  /** HMAC-SHA256 secret for KV integrity signing */
  kvSigningSecret: string;
}

// ---------------------------------------------------------------------------
// Zod validation for KV data
// ---------------------------------------------------------------------------

const triggerIndexEntrySchema = z.object({
  workflowId: z.string(),
  workflowVersion: z.number().int().min(1),
  reentryPolicy: z.enum(REENTRY_POLICIES),
  consentRequirements: z.array(z.enum(CONSENT_REQUIREMENTS)),
  filter: z.record(z.unknown()).optional(),
});

const triggerIndexValueSchema = z.record(z.array(triggerIndexEntrySchema));

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

const KEY_PREFIX = "triggers";

function buildKey(tenantId: string): string {
  return `${KEY_PREFIX}:${tenantId}`;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Creates a KV-backed trigger index.
 *
 * KV key format: `triggers:{tenantId}` (single key per tenant)
 * KV value: JSON object mapping event types to TriggerIndexEntry arrays
 *
 * - `lookup` reads the single key, returns entries for the requested event type.
 * - `rebuild` fetches entries via `triggerSource`, writes a single key with TTL.
 * - `invalidate` deletes the single key.
 */
export function createKVTriggerIndex(
  kv: KVNamespaceLike,
  config: KVTriggerIndexConfig,
): TriggerIndex {
  const { triggerSource, kvSigningSecret } = config;

  return {
    async lookup(tenantId: string, eventType: string): Promise<TriggerIndexEntry[]> {
      const result = await signedKvGet<Record<string, TriggerIndexEntry[]>>(kv, buildKey(tenantId), {
        secret: kvSigningSecret,
        schema: triggerIndexValueSchema,
      });
      if (!result) return [];
      return result[eventType] ?? [];
    },

    async rebuild(tenantId: string): Promise<void> {
      // Fetch fresh data from the source
      const entriesByEvent = await triggerSource(tenantId);

      // Build the value object
      const value: Record<string, TriggerIndexEntry[]> = {};
      for (const [eventType, entries] of entriesByEvent) {
        if (entries.length > 0) {
          value[eventType] = entries;
        }
      }

      // Write single key with TTL
      await signedKvPut(kv, buildKey(tenantId), value, kvSigningSecret, { expirationTtl: 3600 });
    },

    async invalidate(tenantId: string): Promise<void> {
      await kv.delete(buildKey(tenantId));
    },
  };
}
